<template>
  <tx-dialog v-model="visible" :title="t('changeLog.title')" width="95%" height="95%">
    <div class="flex flex-col w-full h-full border-2 bg-gray-50">
      <tx-data-table
        v-if="visible" :show-toolbar-static-actions="false" :item-actions="[]" :list-actions="[]" :columns="columns ? columns : []" :data="data" :catalog-independent="true" :hide-column-selection-filter="true" :data-source-types="dataSourceTypes"
        :loading="loadingData" :visible-columns="visibleColumns" :predefined-criteria="predefinedCriteria" :generate-data-fields="changeLogModel ? changeLogModel.generateDataFields : []" :show-generate-data-actions="true" :title="t('changeLog.records')"
        :model-path="changeLogModel ? changeLogModel.modelPath : ''" :total-column-width="totalColumnWidth" :sort-column="{ ChangedDate: false }" :loading-more="isLoadingMore" :generate-current-criteria="generateDataFilter"
        :is-paginated="true" :show-generate-data="true" :pagination-data="{ filterData: createFilterObject(generateDataFilter), exportPageSize: 1000, exportLimit: changeLogExportLimit }" @get-data="generateData" @save-criteria="saveCriteria" @refresh="doRefresh" @load-more-data="onLoadMoreData"
      />
    </div>
  </tx-dialog>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { clone, isArray } from 'lodash-es'
import type { CancelTokenSource } from 'axios'
import axios from 'axios'
import ChangeLogModel from './changeLog'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxDataTable from '@/shared/components/txDataTable'
import type { ITxDataTableColumn, ITxDataTableVisibleColumn } from '@/shared/components/txDataTable/TxDataTable.types'
import { changeLogConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import utils, { CancelToken } from '@/services/utils'
import { createContainer, getContainer, getMyContainers, updateContainer } from '@/api/t1/container'
import { getChangeLogFields, getChangeLogs, getSellerConfiguration } from '@/api/t1/changeLog'
import CatalogConfig from '@/models/catalogConfig'
import { useNotificationStore } from '@/store/notification'

const { t, te } = useI18n()
const userStore = useUserStore()
const notificationsStore = useNotificationStore()
const visible = ref(false)
const columns = ref<ITxDataTableColumn[]>([])
const data = ref<any[]>([])
const loadingData = ref(true)
const visibleColumns = ref<ITxDataTableVisibleColumn[]>([])
const totalColumnWidth = ref(1200)
const changeLogModel = shallowRef<ChangeLogModel>()

const tablePageSize = 1000
const pageNumber = ref(0) // page number starts from 0
const isLoadingMore = ref(false)
const generateDataFilter = ref<Record<string, any>>({})
const changeLogCriteriaContainer = ref<Record<string, any>>({})
const containerId = ref(-1)
const dataSourceTypes: string[] = ['all', 'selected']
let configurations: undefined | CatalogConfig
let cancelTokenSource: CancelTokenSource | null = null
let changeLogExportLimit: number = 5000
const noMoreDataToFetch = ref(false)
const predefinedCriteria = computed(() => {
  return Object.keys(changeLogCriteriaContainer.value).map((o) => {
    return {
      name: o,
      value: changeLogCriteriaContainer.value[o],
    }
  })
})
function generateData(data) {
  generateDataFilter.value = data
  if (changeLogModel.value) {
    columns.value = changeLogModel.value.getDataTableColumns(t, data.OutputFields)
    visibleColumns.value = changeLogModel.value.getDefaultVisibleColumnList(columns.value)
    doRefresh()
  }
}
async function saveCriteria(data, name) {
  const changeLogContainerName = `${changeLogConstants.changeLogCriteriaContainerName}-${userStore.userProfile.Id}`
  if (!changeLogCriteriaContainer.value || containerId.value === -1) {
    // If container doesn't exist, create it
    const criteria = {}
    criteria[name] = data
    await utils.tryAsync(createContainer(userStore.userProfile.Id, changeLogContainerName, JSON.stringify(criteria))).then(() => {
      changeLogCriteriaContainer.value = criteria
    })
  }
  else {
    // If container exists, load it
    const currentValue = clone(changeLogCriteriaContainer.value)
    currentValue[name] = data
    // Update the container
    await updateContainer(userStore.userProfile.Id, containerId.value, changeLogContainerName, JSON.stringify(currentValue)).then(() => {
      changeLogCriteriaContainer.value = currentValue
    })
  }
}
function createFilterObject(dataObject: Record<string, any>) {
  const filterObject: Record<string, any> = {}
  if (changeLogModel.value && changeLogModel.value.generateDataFields && changeLogModel.value.generateDataFields.length) {
    changeLogModel.value.generateDataFields.forEach((field) => {
      if (dataObject[field.key] && dataObject[field.key].toString().trim() !== '') {
        if (field.key === 'Fields' || field.key === 'OutputFields') {
          if (isArray(dataObject[field.key]) && dataObject[field.key].length !== 0) {
            filterObject[field.key] = []
            dataObject[field.key].forEach((key) => {
              const attributeData = field.data?.find(attribute => attribute.SystemName === key)
              if (attributeData) {
                const sysName = attributeData.SystemName.split('|')
                filterObject[field.key].push({ SystemName: sysName[0], FieldType: attributeData.FieldType, TypeId: attributeData.TypeId })
              }
            })
          }
        }
        else {
          if (field.type === 'dateRange') {
            if (isArray(dataObject[field.key]) && dataObject[field.key].length === 2) {
              filterObject.To = dataObject[field.key][1]
              filterObject.From = dataObject[field.key][0]
            }
          }
          else if (field.type === 'textArea') {
            const values = dataObject[field.key].split('\n').map(val => val.trim()).filter(str => str !== '')
            filterObject[field.key] = values
          }
          else {
            filterObject[field.key] = dataObject[field.key]
          }
        }
      }
    })
  }
  return filterObject
}
async function doRefresh(page: number = 0) {
  const filterData = createFilterObject(generateDataFilter.value)
  try {
    // Cancel the previous request if it exists
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Canceled due to new request')
    }

    // Create a new CancelToken
    cancelTokenSource = axios.CancelToken.source()
    if (userStore.userProfile && userStore.userProfile.AccountDetails.OrganizationId) {
      if (page === 0) {
        noMoreDataToFetch.value = false
        data.value = []
        loadingData.value = true
      }
      else {
        isLoadingMore.value = true
      }

      // Fetch data
      await getChangeLogs(userStore.userProfile.AccountDetails.OrganizationId, filterData, page, tablePageSize, cancelTokenSource.token).then((response) => {
        if (!(response instanceof CancelToken) && response && response.data.length) {
          if (response.data.length < tablePageSize) {
            noMoreDataToFetch.value = true
            isLoadingMore.value = false
          }

          const responseData = response.data
          // Merge new records with existing ones
          data.value = [...data.value, ...responseData]
        }
        else {
          noMoreDataToFetch.value = true
          isLoadingMore.value = false
        }
      }).catch((error) => {
        if (axios.isCancel(error)) {
          console.log('Previous request canceled:', error.message)
        }
        else {
          console.error('Request error:', error)
        }
      })
    }
  }
  catch (error) {
    console.error('Error loading data:', error)
    data.value = []
    notificationsStore.addNotification({ message: 'Failed to load data', type: 'Alert' })
    loadingData.value = false
    isLoadingMore.value = false
  }
  finally {
    pageNumber.value = page
    loadingData.value = false
    isLoadingMore.value = false
  }
}

// handle load more code
async function onLoadMoreData() {
  if (!loadingData.value && !isLoadingMore.value && !noMoreDataToFetch.value) {
    pageNumber.value++
    console.log(`Loading page ${pageNumber.value} !!!`)
    await doRefresh(pageNumber.value)
  }
}
async function showDialog() {
  // Find the container that has the list of my recent boards , container name will be appended with the userId to keep it unique for all users
  data.value = []
  if (userStore && userStore.userProfile) {
    const changeLogContainerName = `${changeLogConstants.changeLogCriteriaContainerName}-${userStore.userProfile.Id}`
    const containersList = await utils.tryAsync(getMyContainers())
    if (containersList.success) {
      const container = containersList.result.data.find(f => f.ContainerName === changeLogContainerName)
      if (container) {
        const res = await utils.tryAsync(getContainer(container.Id))
        if (res.success) {
          try {
            containerId.value = res.result.data.Id
            changeLogCriteriaContainer.value = JSON.parse(res.result.data.Value)
          }
          catch (error) {
            console.log(error)
          }
        }
      }
    }
    const configurationsResponse = await getSellerConfiguration(2).catch((e) => {
      console.log(e)
    })
    configurations = new CatalogConfig(configurationsResponse || [], userStore.userProfile.Roles)
    if (configurations && configurations.ChangeLogExportLimit) {
      changeLogExportLimit = configurations.ChangeLogExportLimit
    }
    visible.value = true
    await getChangeLogFields().then((response) => {
      // get from the api the data to create the columns and pass the response to the changeLog
      // savedviews as welll need to send
      changeLogModel.value = new ChangeLogModel(t, te, response.data, configurations ? configurations.ChangeLogCalcFields : [], configurations ? configurations.DefaultChangeLogReportColumns : [])
    }).catch((e) => {
      console.log(e)
      changeLogModel.value = new ChangeLogModel(t, te)
    })
  }
}
defineExpose({
  showDialog,
})
</script>
